import { captureException } from '@sentry/react'

/**
 * Which announcements a provider has already waved away, kept across reloads and logins.
 *
 * Iterable has no server-side dismissal for embedded messages: `/embedded-messaging/events/dismiss`
 * records an *event*, it does not stop the message being served. Iterable's own answer is to change
 * the user's eligibility, which only works if whoever publishes a campaign remembers to add the
 * exclusion — so the guarantee a clinician actually needs ("I pressed Got it, stop showing me this")
 * cannot be bought from Iterable. It is ours to keep.
 *
 * Deliberately **not** in Redux: `redux-persist` writes the store to localStorage and
 * `redux-state-sync` mirrors it across tabs, so growing a slice to hold this would put a returning
 * provider's session at risk for a set of strings nothing else reads.
 */

// Namespaced per app rather than shared, for the same reason `flagOverrides` is: two QuickMD apps
// can be served from one origin in a preview environment.
const STORAGE_KEY = 'providerAnnouncementDismissals'

/**
 * Dismissals are kept per provider, because a clinic workstation is shared. Keyed on the global key
 * the SDK identifies with, so the record follows the account rather than the browser profile.
 */
type Dismissal = {
  /** When it was dismissed, which is what ages the record out. */
  at: number
  /**
   * The campaign the message belonged to, when it had one. Kept beside the message id because
   * Iterable's own dismissal story is *campaign*-shaped — see `ITERABLE_DISMISSED_FIELD` — while
   * the filtering this module does for the renderer is message-shaped. A proof has no campaign.
   */
  campaignId?: string
}

type Dismissals = Record<string, Record<string, Dismissal>>

/**
 * The Iterable user-profile field that carries a provider's dismissals server-side.
 *
 * This name is a **contract with whoever publishes campaigns**, and the code cannot enforce it:
 * writing the field does nothing on its own. Each campaign's eligibility list has to carry a
 * `<field> does not contain <that campaign's id>` criterion, or Iterable keeps serving the message
 * to a provider who has dismissed it — on every device, including the one they dismissed it on if
 * local storage was cleared. The local record above is what makes dismissal work regardless.
 */
export const ITERABLE_DISMISSED_FIELD = 'dismissedAnnouncementCampaigns'

/**
 * An Iterable campaign is long over well before this, and its message ids never come back — so
 * anything older is dead weight. The cap is the backstop for a provider who stays on one machine
 * for years: it bounds the record whatever the dates say.
 */
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000
const MAX_PER_PROVIDER = 200

/**
 * Hand-validated rather than asserted: this is parsed from storage a previous build wrote, so a
 * stale shape has to be dropped rather than handed on as if it were a dismissal record.
 */
function isDismissals(value: unknown): value is Dismissals {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  return Object.values(value).every(
    (entry) =>
      typeof entry === 'object' &&
      entry !== null &&
      !Array.isArray(entry) &&
      Object.values(entry).every(
        (dismissal) =>
          typeof dismissal === 'object' && dismissal !== null && 'at' in dismissal && typeof dismissal.at === 'number',
      ),
  )
}

function read(): Dismissals {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return isDismissals(parsed) ? parsed : {}
  } catch {
    // A corrupt or unavailable store must never stop an announcement rendering; the worst case is
    // that a dismissed message shows once more.
    return {}
  }
}

/** Drops what has aged out, then keeps the most recent ids if a provider is still over the cap. */
function prune(entries: Record<string, Dismissal>, now: number): Record<string, Dismissal> {
  const live = Object.entries(entries).filter(([, dismissal]) => now - dismissal.at < MAX_AGE_MS)
  const kept = live.sort(([, left], [, right]) => right.at - left.at).slice(0, MAX_PER_PROVIDER)
  return Object.fromEntries(kept)
}

/** The message ids this provider has dismissed, newest first. Empty for an unknown provider. */
export function getDismissedMessageIds(userId: string): string[] {
  const entries = read()[userId]
  if (!entries) return []
  return Object.keys(prune(entries, Date.now()))
}

/**
 * Records a dismissal. Fire-and-forget, like the Iterable event it accompanies: a storage failure
 * (private mode, quota) costs the provider one re-display, which is not worth a toast.
 */
export function recordDismissedMessageId(userId: string, messageId: string, campaignId?: number): void {
  try {
    const now = Date.now()
    const all = read()
    const dismissal: Dismissal = campaignId === undefined ? { at: now } : { at: now, campaignId: String(campaignId) }
    const next = prune({ ...all[userId], [messageId]: dismissal }, now)
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...all, [userId]: next }))
  } catch (error) {
    captureException(error)
  }
}

/**
 * Every campaign this provider has dismissed, for the profile field Iterable segments on.
 *
 * Sent as the whole list rather than one id, because `users/update` *replaces* an array field
 * rather than appending to it — so the local record, not Iterable, is the source of truth for
 * what the field should contain.
 *
 * Strings rather than the raw numeric ids: a campaign id typed into Iterable's segment builder is
 * entered as text, and a `does not contain` on a number array against a typed string is the kind
 * of mismatch nobody notices until a dismissed announcement reappears.
 */
export function getDismissedCampaignIds(userId: string): string[] {
  const entries = read()[userId]
  if (!entries) return []
  const ids = Object.values(prune(entries, Date.now()))
    .map((dismissal) => dismissal.campaignId)
    .filter((id) => id !== undefined)
  return [...new Set(ids)]
}

/** Test seam: drops every provider's record. */
export function clearDismissedAnnouncementsForTests(): void {
  window.localStorage.removeItem(STORAGE_KEY)
}
